/**
 * InternsTable — Registre & paie mensuelle des stagiaires / personnel non permanent.
 *
 * Vue tableau compacte avec :
 *  - Sélecteur mois/année
 *  - Colonnes : Nom · Poste · Type · Date arrivée · Forfait · Statut mois · Actions
 *  - Modale "Nouveau" / "Modifier"
 *  - Actions : marquer payé / annuler paiement / éditer / supprimer
 *  - KPI en-tête : nb actifs, total dû, total payé, restant à payer
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, X, Check, Users, Loader2, RefreshCw,
  ChevronLeft, ChevronRight, Phone, Briefcase, CalendarDays,
  CheckCircle2, MinusCircle, MoreVertical, GraduationCap, FileDown, Receipt,
} from "lucide-react";
import { printInternsMonthPDF, printInternReceiptPDF } from "../utils/salaryPdfHelpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import MarkPaidDialog from "./MarkPaidDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");
const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const KIND_LABEL = { stagiaire: "Stagiaire", journalier: "Journalier", occasionnel: "Occasionnel" };
const ALLOWANCE_LABEL = { monthly: "/mois", daily: "/jour", lumpsum: "forfait" };

const emptyForm = () => ({
  name: "",
  position: "Stagiaire",
  kind: "stagiaire",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  allowance_amount: 0,
  allowance_type: "monthly",
  phone: "",
  notes: "",
  is_active: true,
});

const InternsTable = ({ currentUser }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState({ interns: [], count: 0, total_due: 0, total_paid: 0, total_unpaid: 0 });
  const [loading, setLoading] = useState(false);

  // CRUD modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Mark-paid dialog
  const [markPaidTarget, setMarkPaidTarget] = useState(null);

  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const canEdit = isAdmin || isManager;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/interns/summary`, { params: { year, month } });
      setSummary(res.data);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  const openNew = () => { setForm(emptyForm()); setEditingId(null); setShowModal(true); };
  const openEdit = (i) => {
    setEditingId(i.id);
    setForm({
      name: i.name || "",
      position: i.position || "Stagiaire",
      kind: i.kind || "stagiaire",
      start_date: (i.start_date || "").slice(0, 10),
      end_date: (i.end_date || "").slice(0, 10),
      allowance_amount: i.allowance_amount || 0,
      allowance_type: i.allowance_type || "monthly",
      phone: i.phone || "",
      notes: i.notes || "",
      is_active: i.is_active !== false,
    });
    setShowModal(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      if (editingId) {
        await axios.patch(`${API}/interns/${editingId}`, form);
        toast.success("Fiche mise à jour");
      } else {
        await axios.post(`${API}/interns`, form);
        toast.success("Stagiaire ajouté");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const deleteIntern = async (id) => {
    if (!confirm("Supprimer définitivement cette fiche ?")) return;
    try {
      await axios.delete(`${API}/interns/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const markPaid = (i) => {
    setMarkPaidTarget(i);
  };

  const confirmMarkPaid = async ({ payment_date, amount, paid_by, notes }) => {
    if (!markPaidTarget) return;
    try {
      await axios.post(`${API}/interns/${markPaidTarget.id}/mark-paid`, {
        year, month, amount, payment_date, paid_by, note: notes,
      });
      toast.success(`Paiement enregistré : ${fmt(amount)} F le ${new Date(payment_date).toLocaleDateString("fr-FR")}`);
      setMarkPaidTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const unmarkPaid = async (i) => {
    if (!confirm(`Annuler le paiement du ${MONTHS[month - 1]} ${year} ?`)) return;
    try {
      await axios.post(`${API}/interns/${i.id}/unmark-paid`, { year, month });
      toast.success("Paiement annulé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const kpis = useMemo(() => ([
    { label: "Actifs ce mois", value: summary.count || 0, accent: "#38bdf8" },
    { label: "Total dû", value: `${fmt(summary.total_due)} F`, accent: "#818cf8" },
    { label: "Total payé", value: `${fmt(summary.total_paid)} F`, accent: "#10b981" },
    { label: "Restant", value: `${fmt(summary.total_unpaid)} F`, accent: "#f59e0b" },
  ]), [summary]);

  return (
    <div className="space-y-3" data-testid="interns-table">
      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5 text-indigo-300" strokeWidth={2.3} />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-[15px] leading-tight">Stagiaires & Non permanents</p>
            <p className="text-slate-500 text-[11px] leading-tight">Registre & suivi de paiement mensuel · Espace Maxo</p>
          </div>
        </div>

        {/* Month navigation */}
        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg px-1 py-0.5 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={prevMonth} className="h-7 w-7 p-0 text-slate-300" data-testid="interns-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-[12px] font-bold text-white px-2 min-w-[110px] text-center">
            {MONTHS[month - 1]} {year}
          </div>
          <Button size="sm" variant="ghost" onClick={nextMonth} className="h-7 w-7 p-0 text-slate-300" data-testid="interns-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Button onClick={load} disabled={loading} size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700 h-9 flex-shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>

        <Button
          onClick={() => printInternsMonthPDF(summary, year, month)}
          disabled={loading || summary.interns.length === 0}
          size="sm"
          className="h-9 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11.5px] flex-shrink-0"
          title={`PDF paie stagiaires ${MONTHS[month - 1]} ${year}`}
          data-testid="interns-pdf-month"
        >
          <FileDown className="w-3.5 h-3.5 mr-1" strokeWidth={2.8} /> PDF mois
        </Button>

        {canEdit && (
          <Button onClick={openNew} className="h-9 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex-shrink-0" data-testid="interns-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau
          </Button>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-lg bg-slate-800/60 border border-slate-700 p-2.5"
            style={{ borderLeft: `3px solid ${k.accent}` }}
          >
            <p className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">{k.label}</p>
            <p className="text-white font-black text-[15px] font-mono mt-0.5">{typeof k.value === "number" ? k.value : k.value}</p>
          </div>
        ))}
      </div>

      {/* Info règle métier — décalage de paiement */}
      {summary.payment_planned_date && (
        <div
          className="rounded-lg bg-blue-500/10 border border-blue-500/30 px-3 py-2 flex items-start gap-2"
          data-testid="interns-payment-rule-banner"
        >
          <CalendarDays className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" />
          <div className="text-[11.5px] text-blue-200 leading-tight">
            <b>Mois de travail :</b> {MONTHS[month - 1]} {year} · <b>Payable le</b>{" "}
            <b className="text-emerald-300 font-mono">
              {new Date(summary.payment_planned_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </b>
            <span className="text-slate-400 ml-2">(le 10 du mois suivant — modifiable)</span>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-10 text-center text-slate-400">Chargement…</div>
      ) : summary.interns.length === 0 ? (
        <div className="rounded-lg border border-slate-700 bg-slate-800/40 py-10 text-center">
          <GraduationCap className="w-10 h-10 mx-auto mb-2 opacity-40 text-slate-300" />
          <p className="text-slate-300 text-sm">Aucun stagiaire ou personnel non permanent enregistré.</p>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-slate-600 text-slate-200"
              onClick={openNew}
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter le premier
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-slate-900/60 text-slate-300 text-[10px] uppercase tracking-wider">
                <th className="text-left px-3 py-2 font-bold">Nom · Poste</th>
                <th className="text-left px-2 py-2 font-bold">Type</th>
                <th className="text-left px-2 py-2 font-bold">Arrivée</th>
                <th className="text-right px-2 py-2 font-bold">Forfait</th>
                <th className="text-left px-2 py-2 font-bold">Statut {MONTHS[month - 1].slice(0, 3)}.</th>
                <th className="text-right px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {summary.interns.map((i) => {
                const paid = i.paid_this_month;
                const inactiveThisMonth = !i.active_this_month;
                return (
                  <tr
                    key={i.id}
                    className={`border-t border-slate-700/50 hover:bg-slate-700/20 ${inactiveThisMonth ? "opacity-60" : ""}`}
                    data-testid={`intern-row-${i.id}`}
                  >
                    <td className="px-3 py-2">
                      <div className="text-white font-semibold text-[12.5px]">{i.name}</div>
                      <div className="text-slate-500 text-[10.5px] flex items-center gap-1 mt-0.5">
                        <Briefcase className="w-3 h-3" /> {i.position || "—"}
                        {i.phone && (<> <span className="text-slate-700">·</span> <Phone className="w-2.5 h-2.5" /> <span>{i.phone}</span></>)}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-block text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        i.kind === "journalier" ? "bg-amber-500/15 text-amber-300" :
                        i.kind === "occasionnel" ? "bg-purple-500/15 text-purple-300" :
                        "bg-indigo-500/15 text-indigo-300"
                      }`}>
                        {KIND_LABEL[i.kind] || i.kind}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-slate-300 text-[11px] font-mono whitespace-nowrap">
                      {(i.start_date || "").slice(0, 10) || "—"}
                      {i.end_date && (
                        <div className="text-slate-500 text-[10px]">→ {i.end_date.slice(0, 10)}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <span className="text-white font-bold font-mono text-[12.5px]">{fmt(i.allowance_amount)} F</span>
                      <span className="text-slate-500 text-[10px] ml-1">{ALLOWANCE_LABEL[i.allowance_type] || ""}</span>
                    </td>
                    <td className="px-2 py-2">
                      {inactiveThisMonth ? (
                        <span className="text-slate-500 text-[10.5px] italic">Hors période</span>
                      ) : paid ? (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 text-emerald-300 text-[11px] font-bold">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Payé · {fmt(i.paid_amount)} F</span>
                          </div>
                          {i.payment_date && (
                            <span className="text-emerald-400 text-[10px] font-mono ml-4">
                              le {new Date(i.payment_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-amber-300 text-[11px] font-bold">
                          <MinusCircle className="w-3.5 h-3.5" /> Non payé
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!canEdit ? (
                        <span className="text-slate-500 text-[10.5px]">—</span>
                      ) : paid ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => printInternReceiptPDF(i, year, month)}
                            className="h-7 px-2 text-sky-300 hover:bg-sky-500/10 hover:text-sky-200 text-[10.5px]"
                            title="Reçu de paiement (PDF)"
                            data-testid={`intern-receipt-pdf-${i.id}`}
                          >
                            <Receipt className="w-3.5 h-3.5 mr-1" /> Reçu
                          </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-white" data-testid={`intern-menu-${i.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-white">
                            <DropdownMenuItem onClick={() => unmarkPaid(i)} className="cursor-pointer text-amber-300 focus:bg-slate-700" data-testid={`intern-unmark-${i.id}`}>
                              <MinusCircle className="w-4 h-4 mr-2" /> Annuler paiement
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem onClick={() => openEdit(i)} className="cursor-pointer focus:bg-slate-700">
                              <Edit2 className="w-4 h-4 mr-2 text-slate-300" /> Modifier
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem onClick={() => deleteIntern(i.id)} className="cursor-pointer text-rose-300 focus:bg-slate-700">
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" onClick={() => markPaid(i)} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10.5px]" data-testid={`intern-mark-paid-${i.id}`}>
                            <Check className="w-3 h-3 mr-1" /> Payer
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-white" data-testid={`intern-menu-unpaid-${i.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-white">
                              <DropdownMenuItem onClick={() => openEdit(i)} className="cursor-pointer focus:bg-slate-700">
                                <Edit2 className="w-4 h-4 mr-2 text-slate-300" /> Modifier
                              </DropdownMenuItem>
                              {isAdmin && (
                                <DropdownMenuItem onClick={() => deleteIntern(i.id)} className="cursor-pointer text-rose-300 focus:bg-slate-700">
                                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Total row */}
            <tfoot>
              <tr className="border-t-2 border-slate-600 bg-slate-900/40 font-bold">
                <td colSpan={3} className="px-3 py-2 text-slate-300 uppercase text-[10.5px] tracking-wider">Total {MONTHS[month - 1]} {year}</td>
                <td className="px-2 py-2 text-right text-indigo-300 font-mono text-[12.5px]">{fmt(summary.total_due)} F</td>
                <td className="px-2 py-2">
                  <span className="text-emerald-300 text-[11px] font-mono">Payé {fmt(summary.total_paid)}</span>
                  {summary.total_unpaid > 0 && (
                    <div className="text-amber-300 text-[10.5px] font-mono">Reste {fmt(summary.total_unpaid)}</div>
                  )}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal CRUD */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg" data-testid="intern-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-300">
              <GraduationCap className="w-5 h-5" />
              {editingId ? "Modifier la fiche" : "Nouveau stagiaire / non permanent"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Registre simplifié : nom, poste, dates, forfait. Sans CNSS/ITS (pas un contrat de travail salarié).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300 text-[11px]">Nom complet *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-slate-700/60 border-slate-600 text-white h-9"
                placeholder="Ex : Christian TOSSOU"
                data-testid="intern-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300 text-[11px]">Poste</Label>
                <Input
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                  className="bg-slate-700/60 border-slate-600 text-white h-9"
                  placeholder="Ex : Serveur stagiaire"
                  data-testid="intern-position-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-[11px]">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="bg-slate-700/60 border-slate-600 text-white h-9" data-testid="intern-kind-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="stagiaire">Stagiaire</SelectItem>
                    <SelectItem value="journalier">Journalier</SelectItem>
                    <SelectItem value="occasionnel">Occasionnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300 text-[11px]">Date d'arrivée</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-slate-700/60 border-slate-600 text-white h-9"
                  data-testid="intern-start-date"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-[11px]">Date de fin (optionnel)</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-slate-700/60 border-slate-600 text-white h-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300 text-[11px]">Frais forfaitaires (FCFA)</Label>
                <Input
                  type="number"
                  value={form.allowance_amount || ""}
                  onChange={(e) => setForm({ ...form, allowance_amount: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-700/60 border-slate-600 text-white h-9"
                  placeholder="25000"
                  data-testid="intern-allowance-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-[11px]">Périodicité</Label>
                <Select value={form.allowance_type} onValueChange={(v) => setForm({ ...form, allowance_type: v })}>
                  <SelectTrigger className="bg-slate-700/60 border-slate-600 text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="daily">Journalier</SelectItem>
                    <SelectItem value="lumpsum">Forfait unique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-[11px]">Téléphone (optionnel)</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-slate-700/60 border-slate-600 text-white h-9"
                placeholder="+229 …"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-[11px]">Notes (optionnel)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-slate-700/60 border-slate-600 text-white min-h-[50px] text-[13px]"
                placeholder="Précisions : mission, dates de congé, contexte…"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4 accent-indigo-500"
              />
              <span className="text-slate-300 text-[12px]">Actif (comptera dans la liste active)</span>
            </label>

            <div className="flex gap-2 pt-1">
              <Button onClick={saveForm} className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-10" data-testid="intern-save-btn">
                <Check className="w-4 h-4 mr-2" /> {editingId ? "Enregistrer" : "Créer"}
              </Button>
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-300 h-10">
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark-paid dialog with configurable payment date */}
      <MarkPaidDialog
        open={!!markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onConfirm={confirmMarkPaid}
        year={year}
        month={month}
        employeeName={markPaidTarget?.name || "—"}
        suggestedAmount={markPaidTarget?.allowance_amount || 0}
        paidBy={currentUser?.name || currentUser?.username || "Administrateur"}
      />
    </div>
  );
};

export default InternsTable;
